import type { Rng } from "./rng";
import { createSeededRng } from "./rng";
import type { RevealSchedule, RevealStep } from "./types";

/**
 * Planning de révélation (ENG-007, ENG-008, ENG-009, README §5).
 *
 * L'ordre est déterministe pour une graine donnée, sérialisable, et partagé par
 * les révélations automatiques **et** les aides : une action du joueur avance
 * dans cet ordre, elle ne choisit jamais une autre position (ADR-0001 §D1).
 */

export type RevealBucket = {
  readonly minLength: number;
  readonly maxLength: number;
  readonly firstDelayMs: number;
  readonly intervalMs: number;
  readonly scoreWindowMs: number;
};

/** Longueur normalisée minimale d'une réponse jouable. */
export const MIN_ANSWER_LENGTH = 4;

/** Part maximale des positions révélées automatiquement, en pourcentage. */
export const MAX_AUTO_REVEAL_RATIO_PERCENT = 65;

/** Nombre de positions qui restent cachées quoi qu'il arrive. */
export const MIN_HIDDEN_POSITIONS = 2;

/** Au-delà de cette longueur, une étape révèle deux positions (ENG-009). */
export const MULTI_REVEAL_MIN_LENGTH = 12;

/**
 * Temporisation des révélations, par palier de longueur (ADR-0001 §D14).
 *
 * Trois principes :
 *
 * 1. **Le premier délai est long, et de loin.** Il couvre la lecture de la
 *    définition *et* le temps d'y réfléchir — deux choses distinctes qu'une
 *    première version confondait. À sept secondes, la lettre tombait pendant
 *    qu'on lisait encore, signalé en session sur une définition de quatorze
 *    mots. Il est désormais trois fois plus long que l'intervalle.
 *
 * 2. **Le jeu accélère ensuite.** Une fois la définition digérée, l'attente
 *    n'apporte plus rien : les révélations se resserrent. C'est le premier
 *    palier qui protège la réflexion, pas les suivants.
 *
 * 3. **Plus le mot est court, plus l'intervalle reste large.** Une lettre vaut
 *    un quart d'un mot de quatre lettres et un quinzième d'un mot de quinze :
 *    le même délai n'y donne pas du tout la même aide.
 *
 * La fenêtre de score suit : elle couvre la dernière révélation plus le temps
 * de conclure, sinon allonger les délais ferait mécaniquement chuter les scores.
 */
export const REVEAL_BUCKETS: readonly RevealBucket[] = [
  { minLength: 4, maxLength: 5, firstDelayMs: 12000, intervalMs: 4000, scoreWindowMs: 26000 },
  { minLength: 6, maxLength: 7, firstDelayMs: 12500, intervalMs: 3900, scoreWindowMs: 28000 },
  { minLength: 8, maxLength: 9, firstDelayMs: 13000, intervalMs: 3800, scoreWindowMs: 31000 },
  { minLength: 10, maxLength: 11, firstDelayMs: 13500, intervalMs: 3700, scoreWindowMs: 34000 },
  { minLength: 12, maxLength: 14, firstDelayMs: 14000, intervalMs: 3600, scoreWindowMs: 38000 },
  {
    minLength: 15,
    maxLength: Number.POSITIVE_INFINITY,
    firstDelayMs: 14500,
    intervalMs: 3500,
    scoreWindowMs: 44000,
  },
];

export function getRevealBucket(normalizedLength: number): RevealBucket {
  if (!Number.isInteger(normalizedLength) || normalizedLength < MIN_ANSWER_LENGTH) {
    throw new Error(
      `Longueur normalisée invalide : ${normalizedLength} (minimum ${MIN_ANSWER_LENGTH})`,
    );
  }

  const bucket = REVEAL_BUCKETS.find(
    (candidate) =>
      normalizedLength >= candidate.minLength && normalizedLength <= candidate.maxLength,
  );

  // Le dernier bucket est ouvert : cette branche est inatteignable.
  if (bucket === undefined) throw new Error(`Aucun bucket pour la longueur ${normalizedLength}`);
  return bucket;
}

/**
 * Nombre maximal de positions révélables, aides comprises.
 *
 * Conséquence directe : le jeu ne résout jamais le mot à la place du joueur,
 * et donc une partie ne se termine jamais d'elle-même (ADR-0001 §D2).
 */
export function maxAutoRevealCount(normalizedLength: number): number {
  const ratioCap = Math.floor((normalizedLength * MAX_AUTO_REVEAL_RATIO_PERCENT) / 100);
  const hiddenCap = normalizedLength - MIN_HIDDEN_POSITIONS;
  return Math.max(0, Math.min(ratioCap, hiddenCap));
}

export function positionsPerStep(normalizedLength: number): number {
  return normalizedLength >= MULTI_REVEAL_MIN_LENGTH ? 2 : 1;
}

/**
 * Ordre officiel par défaut, pour un mot dont le contenu n'en fournit pas.
 *
 * 1. une **première position tirée par la graine du mot** ;
 * 2. ensuite la position maximisant la distance minimale aux positions déjà
 *    révélées, ce qui écarte de fait toute position adjacente tant qu'il reste
 *    une position isolée ;
 * 3. égalités tranchées par le même générateur injecté.
 *
 * Le tirage porte sur la graine, pas sur le hasard d'une partie : un mot donné
 * a toujours le même ordre, et deux joueurs comparent bien la même énigme.
 *
 * **Ceci remplace DECISIONS-01 §F1** (ADR-0001 §D13), qui imposait la première
 * puis la dernière position. Le raisonnement tenait — l'attaque donne la
 * famille du mot, la terminaison donne l'accord — mais il produisait la même
 * ouverture sur les 1 514 entrées, et offrait d'office l'initiale, qui est la
 * lettre la plus informative d'un mot français.
 */
export function generateRevealOrder(input: {
  readonly normalizedLength: number;
  readonly budget: number;
  readonly rng: Rng;
}): number[] {
  const { normalizedLength, budget, rng } = input;
  if (budget <= 0 || normalizedLength <= 0) return [];

  const first = Math.min(normalizedLength - 1, Math.floor(rng() * normalizedLength));
  const order: number[] = [first];

  while (order.length < budget) {
    const chosen = new Set(order);
    let bestDistance = -1;
    let tied: number[] = [];

    for (let position = 0; position < normalizedLength; position += 1) {
      if (chosen.has(position)) continue;

      let distance = Number.POSITIVE_INFINITY;
      for (const taken of order) {
        distance = Math.min(distance, Math.abs(position - taken));
      }

      if (distance > bestDistance) {
        bestDistance = distance;
        tied = [position];
      } else if (distance === bestDistance) {
        tied.push(position);
      }
    }

    const pick = tied[Math.floor(rng() * tied.length)];
    if (pick === undefined) break;
    order.push(pick);
  }

  return order.slice(0, budget);
}

export function buildRevealSchedule(input: {
  readonly normalizedLength: number;
  readonly seed: string;
  /** Ordre imposé par le contenu. À défaut, engendré depuis la graine. */
  readonly order?: readonly number[];
}): RevealSchedule {
  const { normalizedLength, seed } = input;
  const bucket = getRevealBucket(normalizedLength);
  const maxAutoReveals = maxAutoRevealCount(normalizedLength);
  const perStep = positionsPerStep(normalizedLength);

  const order =
    input.order ??
    generateRevealOrder({
      normalizedLength,
      budget: maxAutoReveals,
      rng: createSeededRng(seed),
    });

  const steps: RevealStep[] = [];
  let assigned = 0;
  while (assigned < maxAutoReveals) {
    const count = Math.min(perStep, maxAutoReveals - assigned);
    steps.push({ atMs: bucket.firstDelayMs + steps.length * bucket.intervalMs, count });
    assigned += count;
  }

  return {
    version: 1,
    seed,
    normalizedLength,
    firstDelayMs: bucket.firstDelayMs,
    intervalMs: bucket.intervalMs,
    scoreWindowMs: bucket.scoreWindowMs,
    maxAutoReveals,
    positionsPerStep: perStep,
    order,
    steps,
  };
}

/**
 * Nombre de positions que le planning **automatique** veut voir révélées à cet
 * instant de l'horloge de révélation. Les aides s'y ajoutent séparément.
 */
export function autoRevealTargetAt(schedule: RevealSchedule, revealClockMs: number): number {
  let total = 0;
  for (const step of schedule.steps) {
    if (step.atMs > revealClockMs) break;
    total += step.count;
  }
  return total;
}

/**
 * Nombre d'étapes déjà déclenchées.
 *
 * Une étape n'est pas une lettre : à partir de douze lettres, une étape en porte
 * deux (DECISIONS-01 §B3). La jauge compte les étapes, le libellé compte les lettres.
 */
export function consumedStepCount(schedule: RevealSchedule, revealClockMs: number): number {
  let count = 0;
  for (const step of schedule.steps) {
    if (step.atMs > revealClockMs) break;
    count += 1;
  }
  return count;
}

/** Instant de la prochaine étape automatique, ou `null` si le planning est épuisé. */
export function nextStepAtMs(schedule: RevealSchedule, revealClockMs: number): number | null {
  for (const step of schedule.steps) {
    if (step.atMs > revealClockMs) return step.atMs;
  }
  return null;
}

/** Instant de l'étape automatique précédente, `0` avant la première. */
export function previousStepAtMs(schedule: RevealSchedule, revealClockMs: number): number {
  let previous = 0;
  for (const step of schedule.steps) {
    if (step.atMs > revealClockMs) break;
    previous = step.atMs;
  }
  return previous;
}

/**
 * Positions révélées d'après le seul ordre officiel, sans joueur.
 *
 * Sert à l'outillage éditorial — prévisualisation d'une énigme, filtre de
 * sélection du lot de test — qui juge un mot indépendamment de toute partie.
 * En jeu, la référence est `state.revealedPositions`.
 */
export function revealedPositions(
  schedule: RevealSchedule,
  revealedCount: number,
): readonly number[] {
  return schedule.order.slice(0, Math.max(0, Math.min(revealedCount, schedule.order.length)));
}

/**
 * Prochaine position à révéler, en tenant compte de ce que le joueur a écrit.
 *
 * L'ordre officiel reste la référence, mais une position déjà remplie est
 * sautée. Révéler une lettre là où le joueur a déjà écrit ne lui apprend rien
 * et lui donne le sentiment qu'on lui reprend son travail — d'autant que la
 * première position de l'ordre est l'initiale du mot, là où tout le monde
 * commence à taper. La variante B pousse justement à proposer tôt : sans ce
 * saut, elle gaspillerait ses révélations sur les joueurs qui font exactement
 * ce qu'elle cherche à provoquer.
 *
 * Le saut ne regarde **jamais** si la lettre saisie est juste, seulement si la
 * case est occupée. Sauter les seules lettres correctes reviendrait à dire au
 * joueur lesquelles le sont : la variante A donnerait alors le retour lettre
 * par lettre qu'elle est précisément censée ne pas donner, et la Gate G1
 * comparerait deux fois la même chose.
 *
 * Quand toutes les cases sont occupées, la révélation en reprend une, dans
 * l'ordre officiel, et y met la vérité. Ce n'est pas une perte pour le joueur :
 * si sa lettre était juste, rien ne change à l'écran ; si elle était fausse,
 * elle est corrigée. La grille pleine est l'état normal entre deux essais,
 * puisqu'une proposition fausse ne l'efface pas — sans cette reprise, les
 * révélations s'arrêteraient définitivement au premier essai complet.
 */
export function nextRevealPosition(
  schedule: RevealSchedule,
  alreadyRevealed: readonly number[],
  input: readonly (string | null)[],
): number | null {
  const revealed = new Set(alreadyRevealed);
  const isFree = (position: number) =>
    !revealed.has(position) && (input[position] ?? null) === null;

  // 1. L'ordre officiel tant qu'il propose une case libre.
  const preferred = schedule.order.find(isFree);
  if (preferred !== undefined) return preferred;

  // 2. L'ordre officiel ne contient que les positions révélables : quand elles
  //    sont toutes prises ou occupées, il ne reste rien à y piocher. On reprend
  //    alors la règle d'écartement sur le reste du mot, pour ne pas agglutiner
  //    les révélations en un fragment lisible.
  const spread = farthestFreePosition(input.length, alreadyRevealed, isFree);
  if (spread !== null) return spread;

  // 3. Plus une seule case libre : on en reprend une, dans l'ordre officiel.
  const taken = schedule.order.find((position) => !revealed.has(position));
  if (taken !== undefined) return taken;

  for (let position = 0; position < input.length; position += 1) {
    if (!revealed.has(position)) return position;
  }
  return null;
}

/**
 * Position libre la plus éloignée de celles déjà révélées, égalité au plus
 * petit indice. Même règle que `generateRevealOrder`, mais sans tirage : un
 * repli doit rester reproductible à l'identique lors d'un rejeu du journal.
 */
function farthestFreePosition(
  length: number,
  chosen: readonly number[],
  isFree: (position: number) => boolean,
): number | null {
  let best: number | null = null;
  let bestDistance = -1;

  for (let position = 0; position < length; position += 1) {
    if (!isFree(position)) continue;

    let distance = Number.POSITIVE_INFINITY;
    for (const taken of chosen) distance = Math.min(distance, Math.abs(position - taken));

    if (distance > bestDistance) {
      bestDistance = distance;
      best = position;
    }
  }

  return best;
}
